/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { Card, Col, Descriptions, Drawer, Dropdown, Input, Menu, message, Row, Space, Table } from "antd";
import { CheckOutlined, CloseOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import Flex from "components/shared-components/Flex";
import utils from "utils";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import Loading from "../../../../../components/shared-components/Loading";
import { useTranslation } from "react-i18next";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { isMobile } from "../../../../../functions/browser";
import dayjs from "dayjs";
import { getAllExactCustomers } from "../../../../../functions/api/exact";
import { FIRESTORE_CUSTOMERS_TABLE } from "../../../../../constants/FirestoreConstant";
import { DEFAULT_DELIVERY_COST, FREE_DELIVERY_FROM_PRICE } from "../../../../../constants/DeliveryConstants";
import { USER_STATUS_ACTIVE } from "../../../../../constants/UserConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { PAYMENT_METHOD_DIRECT_DEBIT, PAYMENT_METHOD_INVOICE } from "../../../../../constants/PaymentConstants";
import { useCustomers } from "../../../../../data/hooks/firestore/useCustomers";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";

const ExactCompanyList = () => {
  const { t } = useTranslation();
  const { allCustomers: companies, isLoading: companiesLoading } = useCustomers();
  const { isAuthenticated } = useAuth();
  const { getCountryDelivery } = useCountries();
  const defaultDeliverySettings = getCountryDelivery(COMPANY_COUNTRY_CODE);
  const [drawerVisible, setDrawerVisible] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState(null);
  const [allExactCompanies, setAllExactCompanies] = useState(null);
  const [companiesRetrieved, setCompaniesRetrieved] = useState(false);
  const [company, setCompany] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (!companiesRetrieved && isAuthenticated) {
      setIsLoading(true);
      getFirebaseIDToken().then((token) => {
        getAllExactCustomers(token)
          .then((data) => {
            setFilteredCompanies(data);
            setAllExactCompanies(data);
            setCompaniesRetrieved(true);
            setIsLoading(false);
          })
          .catch((err) => {
            console.log("err: ", err);
            setIsLoading(false);
            message.error(t("notifications.error_retrieving_exact_companies"));
          });
      });
    }
  }, [isAuthenticated]);

  const showDrawer = (row) => {
    setDrawerVisible(true);
    setCompany(row);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setCompany(null);
  };

  const dropdownMenu = (row) => (
    <Menu>
      <Menu.Item>
        <a onClick={() => showDrawer(row)}>
          <Flex alignItems="center">
            <EyeOutlined />
            <span style={{ marginLeft: 4 }}>{t("details")}</span>
          </Flex>
        </a>
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: t("name"),
      dataIndex: "AccountName",
      sorter: (a, b) => utils.antdTableSorter(a, b, "AccountName"),
    },
    {
      title: t("email"),
      dataIndex: "EmailAddress",
    },
    {
      title: t("customer_id"),
      dataIndex: "AccountCode",
    },
    {
      title: t("in_database"),
      render: (data) => {
        const found = !!companies && companies.find((company) => company.exactAccountCode === data.AccountCode);
        return (
          <span>
            {found ? (
              <CheckOutlined style={{ color: "green" }} />
            ) : (
              <Dropdown
                placement="bottomRight"
                overlay={
                  <Menu>
                    <Menu.Item onClick={() => handleSaveToDatabase(data)}>{t("save_to_database")}</Menu.Item>
                  </Menu>
                }
                trigger={["hover"]}
              >
                <CloseOutlined style={{ color: "red", cursor: "pointer" }} />
              </Dropdown>
            )}
          </span>
        );
      },
    },
    // {
    //   title: "",
    //   render: (_, elm) => (
    //     <div className="text-right">
    //       <EllipsisDropdown menu={dropdownMenu(elm)} />
    //     </div>
    //   ),
    // },
  ];

  const handleSaveToDatabase = async (client) => {
    const {
      AccountCode,
      AccountName,
      DeliveryAddressline1,
      DeliveryCity,
      DeliveryPostCode,
      EmailAddress,
      ID,
      InvoiceAddress,
      InvoiceAddressPostCode,
      InvoiceCity,
      PaymentCondition,
      PhoneNumber,
    } = client;
    // Seperate letters from numbers
    const deliveryAddress = DeliveryAddressline1 || "";
    const [deliveryStreet, ...rest] = deliveryAddress.split(/\s+(?=\d+)/);
    const deliveryHouseNumber = rest.join(" ").trim();

    const invoiceAddress = InvoiceAddress || "";
    const [invoiceStreet, ...rest2] = invoiceAddress.split(/\s+(?=\d+)/);
    const invoiceHouseNumber = rest2.join(" ").trim();

    const object = {
      customerAddress: `${deliveryStreet}, ${deliveryHouseNumber}, ${DeliveryCity}, ${DeliveryPostCode}`,
      customerBillingAddress:
        !!invoiceStreet && !!invoiceHouseNumber && !!InvoiceCity && !!InvoiceAddressPostCode
          ? `${invoiceStreet}, ${invoiceHouseNumber}, ${InvoiceCity}, ${InvoiceAddressPostCode}`
          : "",
      customerEmail: EmailAddress,
      customerPhone: PhoneNumber || "",
      customerName: AccountName,
      customerOrders: [],
      customerRegion: COMPANY_COUNTRY_CODE,
      country: COMPANY_COUNTRY_CODE,
      customerShoppingCarts: [],
      deliveryCost: defaultDeliverySettings?.defaultDeliveryCost || DEFAULT_DELIVERY_COST,
      deliveryCostThreshold: defaultDeliverySettings?.deliveryCostThreshold || FREE_DELIVERY_FROM_PRICE,
      customerUsers: [],
      zipCode: DeliveryPostCode || "",
      houseNumber: deliveryHouseNumber || "",
      street: deliveryStreet || "",
      city: DeliveryCity || "",
      invoiceCity: InvoiceCity || "",
      invoiceZipCode: InvoiceAddressPostCode || "",
      invoiceHouseNumber: invoiceHouseNumber || "",
      invoiceStreet: invoiceStreet || "",
      paymentMethod: PaymentCondition === "CD" ? PAYMENT_METHOD_DIRECT_DEBIT : PAYMENT_METHOD_INVOICE,
      status: USER_STATUS_ACTIVE,
      exactAccountCode: AccountCode,
      exactId: ID,
      createdAt: dayjs().unix(),
    };

    try {
      // Add document to collection
      const customersCollectionRef = collection(firestore, FIRESTORE_CUSTOMERS_TABLE);
      const docRef = await addDoc(customersCollectionRef, object);

      // Save document id to object as customerId
      try {
        const customerDocRef = doc(firestore, FIRESTORE_CUSTOMERS_TABLE, docRef.id);
        await updateDoc(customerDocRef, { customerId: docRef.id });
      } catch (err) {
        console.log(err);
      }

      message.success(t("notifications.client_saved_to_database"));
      onSearch(searchValue);
    } catch (err) {
      console.log(err);
    }
  };

  const onSearch = (value) => {
    const data = utils.wildCardSearchOnExactCustomer(allExactCompanies, value);
    setSearchValue(value);
    setFilteredCompanies(data);
  };

  if (!isLoading && !companiesLoading)
    return (
      <Row className={"mb-2"}>
        <Col xs={24} md={16} xl={15} xxl={14}>
          <h1>{t("exact_customers")}</h1>
        </Col>
        <Col xs={24}>
          <Card>
            <Flex alignItems="center" justifyContent="between" mobileFlex={false}>
              <Flex className="mb-1" mobileFlex={false}>
                <Space wrap>
                  <Input
                    placeholder={t("search")}
                    prefix={<SearchOutlined />}
                    onChange={(e) => onSearch(e.target.value)}
                  />
                </Space>
              </Flex>
            </Flex>
            <div className="table-responsive">
              <Table
                showSorterTooltip={false}
                columns={tableColumns}
                dataSource={filteredCompanies}
                rowKey="customerId"
                scroll={{ x: 1300 }}
                loading={companiesLoading}
              />
            </div>
            {!!company && (
              <Drawer
                title={t("company_data", { company: company.naam })}
                placement="right"
                onClose={closeDrawer}
                open={drawerVisible}
                width={isMobile ? window.innerWidth : 850}
              >
                <Descriptions
                  bordered
                  title={<div className="flex-row">{t("company_data")}</div>}
                  size="small"
                  column={1}
                >
                  <Descriptions.Item label={t("company")}>{company.naam}</Descriptions.Item>
                  <Descriptions.Item label={t("company_id")}>{company.relatiecode}</Descriptions.Item>
                  <Descriptions.Item label={t("snelstart_id")}>{company.id}</Descriptions.Item>
                  <Descriptions.Item label={t("customer.correspondenceAddress")}>
                    {!!company.correspondentieAdres
                      ? company.correspondentieAdres.straat +
                        ", " +
                        company.correspondentieAdres.postcode +
                        " " +
                        company.correspondentieAdres.plaats
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("customer.businessAddress")}>
                    {!!company.vestigingsAdres
                      ? company.vestigingsAdres.straat +
                        ", " +
                        company.vestigingsAdres.postcode +
                        " " +
                        company.vestigingsAdres.plaats
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("email")}>{!!company.email ? company.email : "-"}</Descriptions.Item>
                  <Descriptions.Item label={t("phone")}>
                    {!!company.telefoon ? company.telefoon : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("discount")}>
                    {!!company.factuurkorting ? company.factuurkorting + "%" : "0%"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("delivery_days")}>
                    {!!company.memo ? company.memo : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Drawer>
            )}
          </Card>
        </Col>
      </Row>
    );
  else return <Loading />;
};

export default ExactCompanyList;
